import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  DatePicker,
  Divider,
  Empty,
  Flex,
  Input,
  InputNumber,
  Modal,
  Select,
  Spin,
  Table,
  Tooltip,
  Upload,
  notification,
} from 'antd';
import {
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FlagOutlined,
  PlusOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { clientsApi, resourceApi } from '../api';
import { userRole, employeeId } from '../../GetRole.utils';
import PhaseEditor, { checkPhases } from './PhaseEditor';
import {
  MAX_FTE,
  MAX_WEEKS,
  exportPlanToExcel,
  parsePlanFile,
  matchEmployee,
  toFte,
} from './resourcePlanExcel';
import './resourcePlan.css';

let uidSeq = 0;
const nextUid = () => `r${++uidSeq}`;

const round = (value) => Math.round(value * 100) / 100;

// Grid state is the editable part of the plan; resources carry a local uid as the row key.
const toGridState = (plan) => ({
  startDate: plan.startDate,
  totalWeeks: plan.totalWeeks,
  phases: plan.phases,
  resources: plan.resources.map((resource) => ({
    uid: nextUid(),
    roleId: resource.roleId,
    empCode: resource.empCode || null,
    allocations: resource.allocations,
  })),
});

// Comparable form of the grid, without the local row keys.
const snapshot = (grid) =>
  JSON.stringify({
    ...grid,
    resources: grid.resources.map(({ uid, ...resource }) => resource),
  });

// Shade deepens up to 2 FTE so cells with several people stay readable.
const fteCellStyle = (fte) =>
  fte > 0 ? { background: `rgba(41, 82, 204, ${0.06 + Math.min(fte, 2) * 0.14})` } : undefined;

const between = (value, a, b) => value >= Math.min(a, b) && value <= Math.max(a, b);

// Like Excel, a fill runs along one axis only: across weeks of the source row,
// or down/up the source week, whichever way the drag has moved further.
const inFillRange = (fill, rowIndex, weekIndex) => {
  if (!fill) return false;
  const vertical = Math.abs(fill.toRow - fill.row) > Math.abs(fill.toWeek - fill.week);
  return vertical
    ? weekIndex === fill.week && between(rowIndex, fill.row, fill.toRow)
    : rowIndex === fill.row && between(weekIndex, fill.week, fill.toWeek);
};

const ResourcePlan = ({ projectId }) => {
  const readOnly = userRole === 'finance';
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [plan, setPlan] = useState(null);
  const [grid, setGrid] = useState(null);
  const [savedSnapshot, setSavedSnapshot] = useState('');
  const [roles, setRoles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [phaseEditorOpen, setPhaseEditorOpen] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Drag-to-fill in progress: source cell { row, week }, cell under the pointer
  // { toRow, toWeek } (row and week indexes) and the value being copied.
  const [fill, setFill] = useState(null);
  const fillRef = useRef(null);
  fillRef.current = fill;
  const isFilling = !!fill;
  // Cell the mouse went down on; it turns into a fill once the pointer moves to another cell.
  const pressedCellRef = useRef(null);

  useEffect(() => {
    const release = () => {
      pressedCellRef.current = null;
    };
    window.addEventListener('mouseup', release);
    return () => window.removeEventListener('mouseup', release);
  }, []);

  const applyPlan = (data) => {
    const next = toGridState(data);
    setPlan(data);
    setGrid(next);
    setSavedSnapshot(snapshot(next));
  };

  const fetchPlan = useCallback(async () => {
    setLoading(true);
    try {
      const response = await resourceApi.getPlan(projectId);
      applyPlan(response.data);
    } catch (err) {
      notification.error({
        message: err?.response?.data?.message || 'Could not load the resource plan.',
      });
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const fetchRoles = () =>
    resourceApi
      .getRoles()
      .then((res) => setRoles(res.data || []))
      .catch((err) => console.log(err));

  useEffect(() => {
    fetchPlan();
  }, [fetchPlan]);

  useEffect(() => {
    fetchRoles();
    clientsApi
      .getEmployeeName()
      .then((res) => setEmployees(res.data || []))
      .catch((err) => console.log(err));
  }, []);

  const isDirty = grid && snapshot(grid) !== savedSnapshot;

  // ------------------------------------------------------------------ edits
  const updateResource = (uid, changes) =>
    setGrid((prev) => ({
      ...prev,
      resources: prev.resources.map((row) => (row.uid === uid ? { ...row, ...changes } : row)),
    }));

  const setFte = (uid, weekIndex, fte) =>
    setGrid((prev) => ({
      ...prev,
      resources: prev.resources.map((row) =>
        row.uid === uid
          ? { ...row, allocations: row.allocations.map((v, i) => (i === weekIndex ? fte : v)) }
          : row
      ),
    }));

  const applyFill = (range) =>
    setGrid((prev) => ({
      ...prev,
      resources: prev.resources.map((row, rowIndex) => ({
        ...row,
        allocations: row.allocations.map((v, i) => (inFillRange(range, rowIndex, i) ? range.value : v)),
      })),
    }));

  // Dropping the fill handle anywhere copies the source value over the dragged cells.
  useEffect(() => {
    if (!isFilling) return undefined;
    const finish = () => {
      const current = fillRef.current;
      if (current && (current.toRow !== current.row || current.toWeek !== current.week)) {
        applyFill(current);
      }
      setFill(null);
    };
    window.addEventListener('mouseup', finish);
    return () => window.removeEventListener('mouseup', finish);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFilling]);

  const addResource = () =>
    setGrid((prev) => ({
      ...prev,
      resources: [
        ...prev.resources,
        { uid: nextUid(), roleId: null, empCode: null, allocations: Array(prev.totalWeeks).fill(0) },
      ],
    }));

  const duplicateResource = (uid) =>
    setGrid((prev) => {
      const index = prev.resources.findIndex((row) => row.uid === uid);
      const copy = { ...prev.resources[index], uid: nextUid(), empCode: null };
      const resources = [...prev.resources];
      resources.splice(index + 1, 0, copy);
      return { ...prev, resources };
    });

  const removeResource = (uid) =>
    setGrid((prev) => ({ ...prev, resources: prev.resources.filter((row) => row.uid !== uid) }));

  // Growing pads every row with zero weeks; shrinking drops the trailing weeks and clips phases.
  const setTotalWeeks = (value) => {
    const totalWeeks = Math.max(1, Math.min(MAX_WEEKS, Math.floor(value || 1)));
    setGrid((prev) => ({
      ...prev,
      totalWeeks,
      phases: prev.phases
        .filter((phase) => phase.startWeek <= totalWeeks)
        .map((phase) => ({ ...phase, endWeek: Math.min(phase.endWeek, totalWeeks) })),
      resources: prev.resources.map((row) => ({
        ...row,
        allocations: Array.from({ length: totalWeeks }, (_, i) => row.allocations[i] || 0),
      })),
    }));
  };

  const addRole = async () => {
    const name = newRoleName.trim();
    if (!name) return;
    try {
      await resourceApi.createRole({ name, empCode: employeeId });
      setNewRoleName('');
      fetchRoles();
      notification.success({ message: `Role "${name}" added.` });
    } catch (err) {
      notification.error({ message: err?.response?.data?.message || 'Could not add the role.' });
    }
  };

  const save = async () => {
    if (grid.resources.some((row) => !row.roleId)) {
      notification.error({ message: 'Pick a role for every row before saving.' });
      return;
    }
    const phaseError = checkPhases(grid.phases, grid.totalWeeks);
    if (phaseError) {
      notification.error({ message: phaseError });
      return;
    }
    setSaving(true);
    try {
      const response = await resourceApi.savePlan(projectId, {
        startDate: grid.startDate,
        totalWeeks: grid.totalWeeks,
        phases: grid.phases,
        resources: grid.resources.map(({ roleId, empCode, allocations }) => ({
          roleId,
          empCode: empCode || null,
          allocations,
        })),
        empCode: employeeId,
      });
      applyPlan(response.data);
      notification.success({ message: 'Resource plan saved.' });
    } catch (err) {
      const message = err?.response?.data?.message;
      notification.error({
        message: typeof message === 'string' ? message : 'Could not save the resource plan.',
      });
    } finally {
      setSaving(false);
    }
  };

  // ------------------------------------------------------------ excel
  const exportExcel = async () => {
    setExporting(true);
    try {
      await exportPlanToExcel({ grid, roles, employees, projectName: plan.project.projectName });
    } catch (err) {
      console.log(err);
      notification.error({ message: 'Could not export the resource plan.' });
    } finally {
      setExporting(false);
    }
  };

  // Reads the file and shows what will change; nothing touches the grid until confirmed.
  const readImportFile = async (file) => {
    try {
      const parsed = await parsePlanFile(file);
      if (parsed.rows.length === 0) {
        notification.error({ message: 'No role rows were found below the week header.' });
        return false;
      }
      const knownRoles = new Map(roles.map((role) => [role.name.toLowerCase(), role]));
      const newRoles = [
        ...new Set(parsed.rows.map((row) => row.roleName).filter((name) => !knownRoles.has(name.toLowerCase()))),
      ];
      const unmatchedPeople = [
        ...new Set(parsed.rows.filter((row) => row.person && !matchEmployee(row.person, employees)).map((row) => row.person)),
      ];
      setImportPreview({ ...parsed, fileName: file.name, newRoles, unmatchedPeople });
    } catch (err) {
      notification.error({ message: err.message || 'Could not read the file.' });
    }
    return false; // keep antd Upload from posting the file anywhere
  };

  const applyImport = async () => {
    const preview = importPreview;
    setImporting(true);
    try {
      for (const name of preview.newRoles) {
        await resourceApi.createRole({ name, empCode: employeeId });
      }
      const roleList = (await resourceApi.getRoles()).data || [];
      setRoles(roleList);
      const roleIdByName = new Map(roleList.map((role) => [role.name.toLowerCase(), role.roleId]));

      setGrid((prev) => ({
        startDate: preview.startDate || prev.startDate,
        totalWeeks: preview.totalWeeks,
        phases: preview.phases,
        resources: preview.rows.map((row) => ({
          uid: nextUid(),
          roleId: roleIdByName.get(row.roleName.toLowerCase()) || null,
          empCode: matchEmployee(row.person, employees),
          allocations: row.allocations,
        })),
      }));
      setImportPreview(null);
      notification.success({ message: 'Plan imported. Review it and click "Save plan" to keep it.' });
    } catch (err) {
      notification.error({ message: err?.response?.data?.message || 'Could not import the plan.' });
    } finally {
      setImporting(false);
    }
  };

  const discard = () => {
    const next = toGridState(plan);
    setGrid(next);
    setSavedSnapshot(snapshot(next));
  };

  // ----------------------------------------------------------------- derived
  const weekTotals = useMemo(() => {
    if (!grid) return [];
    return Array.from({ length: grid.totalWeeks }, (_, i) =>
      round(grid.resources.reduce((sum, row) => sum + (row.allocations[i] || 0), 0))
    );
  }, [grid]);

  const personWeeks = round(weekTotals.reduce((sum, value) => sum + value, 0));
  const peakFte = weekTotals.length ? Math.max(...weekTotals) : 0;

  const roleOptions = roles.map((role) => ({ value: role.roleId, label: role.name }));
  const employeeOptions = employees.map((emp) => ({
    value: emp.empCode,
    label: emp.name ? `${emp.name} (${emp.empCode})` : emp.empCode,
  }));

  const columns = useMemo(() => {
    if (!grid) return [];
    const start = dayjs(grid.startDate);
    const rowIndexByUid = new Map(grid.resources.map((row, index) => [row.uid, index]));

    const weekColumn = (weekNo) => {
      const weekStart = start.add((weekNo - 1) * 7, 'day');
      return {
        key: `w${weekNo}`,
        width: 72,
        align: 'center',
        title: (
          <Tooltip
            title={`${weekStart.format('DD MMM YYYY')} – ${weekStart.add(6, 'day').format('DD MMM YYYY')}`}
          >
            <Flex vertical align="center" style={{ lineHeight: 1.2 }}>
              <span>W{weekNo}</span>
              <span className="rp-week-date">{weekStart.format('DD MMM')}</span>
            </Flex>
          </Tooltip>
        ),
        onCell: (row) => {
          const weekIndex = weekNo - 1;
          const rowIndex = rowIndexByUid.get(row.uid);
          const previewing = inFillRange(fill, rowIndex, weekIndex);
          return {
            className: `rp-fte-cell${previewing ? ' rp-fill-range' : ''}`,
            style: fteCellStyle(previewing ? fill.value : row.allocations[weekIndex]),
            onMouseDown: readOnly
              ? undefined
              : (e) => {
                  if (e.button === 0) {
                    pressedCellRef.current = { row: rowIndex, week: weekIndex, value: row.allocations[weekIndex] || 0 };
                  }
                },
            onMouseEnter: readOnly
              ? undefined
              : (e) => {
                  if (fill) {
                    setFill((prev) => prev && { ...prev, toRow: rowIndex, toWeek: weekIndex });
                    return;
                  }
                  // Pressing on a cell and moving onto another one starts a fill from the pressed cell.
                  const pressed = pressedCellRef.current;
                  if (pressed && e.buttons === 1 && (pressed.row !== rowIndex || pressed.week !== weekIndex)) {
                    document.activeElement?.blur();
                    window.getSelection()?.removeAllRanges();
                    setFill({ ...pressed, toRow: rowIndex, toWeek: weekIndex });
                  }
                },
          };
        },
        render: (_, row) => {
          const weekIndex = weekNo - 1;
          const rowIndex = rowIndexByUid.get(row.uid);
          const fte = inFillRange(fill, rowIndex, weekIndex) ? fill.value : row.allocations[weekIndex] || 0;
          if (readOnly) {
            return <span className={fte ? '' : 'rp-zero'}>{fte}</span>;
          }
          return (
            <>
              <InputNumber
                aria-label={`Week ${weekNo} allocation`}
                className="rp-fte-input"
                variant="borderless"
                size="small"
                controls={false}
                min={0}
                max={MAX_FTE}
                step={0.25}
                placeholder="0"
                value={fte || null}
                onFocus={(e) => e.target.select()}
                onChange={(value) => setFte(row.uid, weekIndex, toFte(value))}
              />
              <span
                className="rp-fill-handle"
                role="presentation"
                title="Drag across weeks or down rows to copy. Double-click to fill to the last week."
                onMouseDown={(e) => {
                  e.preventDefault();
                  setFill({ row: rowIndex, week: weekIndex, toRow: rowIndex, toWeek: weekIndex, value: fte });
                }}
                onDoubleClick={() =>
                  applyFill({ row: rowIndex, week: weekIndex, toRow: rowIndex, toWeek: grid.totalWeeks - 1, value: fte })
                }
              />
            </>
          );
        },
      };
    };

    // Weeks inside a phase are grouped under its name; other weeks get an empty band.
    const weekGroups = [];
    for (let weekNo = 1; weekNo <= grid.totalWeeks; ) {
      const phase = grid.phases.find((p) => p.startWeek === weekNo);
      if (phase) {
        const children = [];
        for (let w = phase.startWeek; w <= phase.endWeek; w++) children.push(weekColumn(w));
        weekGroups.push({ key: `p${weekNo}`, title: phase.name, className: 'rp-phase', children });
        weekNo = phase.endWeek + 1;
      } else {
        weekGroups.push({ key: `p${weekNo}`, title: '', className: 'rp-phase rp-phase-none', children: [weekColumn(weekNo)] });
        weekNo += 1;
      }
    }

    const cols = [
      {
        key: 'role',
        title: 'Role / person',
        fixed: 'left',
        width: 260,
        render: (_, row) =>
          readOnly ? (
            <Flex vertical>
              <b>{roles.find((role) => role.roleId === row.roleId)?.name || '-'}</b>
              <span className="rp-muted">
                {employees.find((emp) => emp.empCode === row.empCode)?.name || row.empCode || 'Unassigned'}
              </span>
            </Flex>
          ) : (
            <Flex vertical gap={4}>
              <Select
                aria-label="Role"
                size="small"
                showSearch
                optionFilterProp="label"
                placeholder="Select role"
                status={row.roleId ? undefined : 'warning'}
                value={row.roleId}
                options={roleOptions}
                onChange={(value) => updateResource(row.uid, { roleId: value })}
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    <Divider style={{ margin: '8px 0' }} />
                    <Flex gap={8} style={{ padding: '0 8px 4px' }}>
                      <Input
                        size="small"
                        placeholder="New role name"
                        value={newRoleName}
                        maxLength={120}
                        onChange={(e) => setNewRoleName(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                      />
                      <Button size="small" icon={<PlusOutlined />} onClick={addRole}>
                        Add
                      </Button>
                    </Flex>
                  </>
                )}
              />
              <Select
                aria-label="Assigned person"
                size="small"
                showSearch
                allowClear
                optionFilterProp="label"
                placeholder="Assign person (optional)"
                value={row.empCode || undefined}
                options={employeeOptions}
                onChange={(value) => updateResource(row.uid, { empCode: value || null })}
              />
            </Flex>
          ),
      },
      ...weekGroups,
      {
        key: 'total',
        title: (
          <Tooltip title="Sum of weekly FTE">
            Person-weeks
          </Tooltip>
        ),
        fixed: 'right',
        width: 100,
        align: 'center',
        render: (_, row) => <b>{round(row.allocations.reduce((sum, value) => sum + value, 0))}</b>,
      },
    ];

    if (!readOnly) {
      cols.push({
        key: 'actions',
        title: '',
        fixed: 'right',
        width: 80,
        render: (_, row) => (
          <Flex>
            <Tooltip title="Duplicate row">
              <Button type="text" size="small" aria-label="Duplicate row" icon={<CopyOutlined />} onClick={() => duplicateResource(row.uid)} />
            </Tooltip>
            <Tooltip title="Remove row">
              <Button type="text" size="small" danger aria-label="Remove row" icon={<DeleteOutlined />} onClick={() => removeResource(row.uid)} />
            </Tooltip>
          </Flex>
        ),
      });
    }
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grid, roles, employees, newRoleName, readOnly, fill]);

  if (!grid) {
    return (
      <Flex justify="center" style={{ padding: 48 }}>
        {loading ? <Spin /> : <Empty description="Resource plan unavailable" />}
      </Flex>
    );
  }

  const scrollX = 260 + grid.totalWeeks * 72 + 100 + (readOnly ? 0 : 80);

  return (
    <Flex vertical gap={16}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
        <Flex align="center" gap={16} wrap="wrap">
          <Flex vertical gap={2}>
            <label htmlFor="rp-start" className="rp-label">Week 1 starts</label>
            <DatePicker
              id="rp-start"
              format="DD/MM/YYYY"
              allowClear={false}
              disabled={readOnly}
              value={dayjs(grid.startDate)}
              onChange={(date) => date && setGrid((prev) => ({ ...prev, startDate: date.format('YYYY-MM-DD') }))}
            />
          </Flex>
          <Flex vertical gap={2}>
            <label htmlFor="rp-weeks" className="rp-label">Weeks</label>
            <InputNumber
              id="rp-weeks"
              min={1}
              max={MAX_WEEKS}
              disabled={readOnly}
              value={grid.totalWeeks}
              onChange={setTotalWeeks}
              style={{ width: 96 }}
            />
          </Flex>
          <Flex gap={24} className="rp-stats">
            <Flex vertical>
              <b>{personWeeks}</b>
              <span>person-weeks</span>
            </Flex>
            <Flex vertical>
              <b>{peakFte}</b>
              <span>peak FTE</span>
            </Flex>
            <Flex vertical>
              <b>{grid.resources.length}</b>
              <span>roles planned</span>
            </Flex>
          </Flex>
        </Flex>
        <Flex gap={8} wrap="wrap">
          <Button
            icon={<DownloadOutlined />}
            onClick={exportExcel}
            loading={exporting}
            disabled={grid.resources.length === 0}
          >
            Export Excel
          </Button>
          {!readOnly && (
            <Upload accept=".xlsx" showUploadList={false} beforeUpload={readImportFile}>
              <Button icon={<UploadOutlined />}>Import Excel</Button>
            </Upload>
          )}
        </Flex>
        {!readOnly && (
          <Flex gap={8} wrap="wrap">
            <Button icon={<FlagOutlined />} onClick={() => setPhaseEditorOpen(true)}>
              Sprints & phases
            </Button>
            <Button icon={<PlusOutlined />} onClick={addResource}>
              Add role
            </Button>
            <Button onClick={discard} disabled={!isDirty || saving}>
              Discard
            </Button>
            <Button type="primary" onClick={save} loading={saving} disabled={!isDirty}>
              Save plan
            </Button>
          </Flex>
        )}
      </Flex>

      <span className="rp-muted">
        {isDirty
          ? 'You have unsaved changes.'
          : plan.isSaved
            ? `Last saved${plan.updatedByName ? ` by ${plan.updatedByName}` : ''} on ${dayjs(plan.updatedAt).format('DD/MM/YYYY HH:mm')}`
            : 'No plan saved yet. Weeks default to the project dates; add roles to start.'}
        {!readOnly && grid.resources.length > 0 && (
          <> · Tip: press on a cell and drag across weeks or down rows to copy its value.</>
        )}
      </span>

      {grid.resources.length === 0 ? (
        <Empty description="No roles planned yet">
          {!readOnly && (
            <Button type="primary" icon={<PlusOutlined />} onClick={addResource}>
              Add role
            </Button>
          )}
        </Empty>
      ) : (
        <Table
          className={`rp-table${fill ? ' rp-filling' : ''}`}
          rowKey="uid"
          bordered
          size="small"
          loading={loading}
          columns={columns}
          dataSource={grid.resources}
          pagination={false}
          scroll={{ x: scrollX }}
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row className="rp-total-row">
                <Table.Summary.Cell index={0}>Total FTE / week</Table.Summary.Cell>
                {weekTotals.map((total, i) => (
                  <Table.Summary.Cell index={i + 1} key={i} align="center">
                    {total}
                  </Table.Summary.Cell>
                ))}
                <Table.Summary.Cell index={grid.totalWeeks + 1} align="center">
                  {personWeeks}
                </Table.Summary.Cell>
                {!readOnly && <Table.Summary.Cell index={grid.totalWeeks + 2} />}
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      )}

      <Modal
        title="Import resource plan"
        open={!!importPreview}
        onCancel={() => setImportPreview(null)}
        onOk={applyImport}
        okText="Import into grid"
        confirmLoading={importing}
      >
        {importPreview && (
          <Flex vertical gap={10}>
            <span>
              <b>{importPreview.fileName}</b>
            </span>
            <ul style={{ margin: 0, paddingInlineStart: 20 }}>
              <li>
                {importPreview.rows.length} role row(s) × {importPreview.totalWeeks} week(s)
              </li>
              <li>{importPreview.phases.length} sprint(s) / phase(s)</li>
              <li>
                Week 1 starts{' '}
                {dayjs(importPreview.startDate || grid.startDate).format('DD MMM YYYY')}
                {!importPreview.startDate && ' (kept from the current plan)'}
              </li>
            </ul>
            {importPreview.newRoles.length > 0 && (
              <span>
                New roles that will be added to the role list:{' '}
                <b>{importPreview.newRoles.join(', ')}</b>
              </span>
            )}
            {importPreview.unmatchedPeople.length > 0 && (
              <span style={{ color: '#B54708' }}>
                Not found in the employee list, left unassigned:{' '}
                {importPreview.unmatchedPeople.join(', ')}
              </span>
            )}
            {importPreview.adjustedCells > 0 && (
              <span style={{ color: '#B54708' }}>
                {importPreview.adjustedCells} cell(s) were adjusted: values must be numbers from 0
                to {MAX_FTE} with up to two decimals.
              </span>
            )}
            <span className="rp-muted">
              This replaces the grid on screen. Nothing is saved until you click "Save plan".
            </span>
          </Flex>
        )}
      </Modal>

      <PhaseEditor
        open={phaseEditorOpen}
        phases={grid.phases}
        totalWeeks={grid.totalWeeks}
        onCancel={() => setPhaseEditorOpen(false)}
        onApply={(phases) => {
          setGrid((prev) => ({ ...prev, phases }));
          setPhaseEditorOpen(false);
        }}
      />
    </Flex>
  );
};

export default ResourcePlan;
